// Identifies an Airbrake project (a dashboard bucket). Each app injects its
// own pair at boot — this lib must stay app-agnostic (Nx dependency direction:
// libs never import from apps), so there are deliberately NO ids or keys in
// this file, and no fallback: an unconfigured notifier fails closed with a
// console warning instead of silently reporting to the wrong project.
//
// Project keys are submission-only credentials that ship in every client
// bundle by design — they are not secrets, which is why per-app in-code
// constants (see AGENTS.md "Environment-conditional config") are appropriate.
export interface AirbrakeProject {
  projectId: number
  projectKey: string
}

// Environment label attached to every notice.
// ONE source of truth: NEXT_PUBLIC_VERCEL_ENV — Vercel sets it automatically
// on every deploy ('production' | 'preview' | 'development'), it works in
// both client bundles (inlined at build) and server code, and it is the same
// variable each app's lib/airbrake.ts already uses to select its project.
// One variable, one bet; no team-managed env var can shadow it.
// Off Vercel (laptop builds, CI, jest) it is unset → 'local'. Those contexts
// either don't report at all (isAirbrakeLive is false) or report to the
// staging project, so a coarse label is exactly enough — and a laptop can
// never masquerade as a real deploy.
export const airbrakeEnvironment = process.env.NEXT_PUBLIC_VERCEL_ENV || 'local'

// Gate still uses NODE_ENV: we want Airbrake active on every production-style
// build (prod deploy, preview/staging deploy, and local `next build && next
// start`). NODE_ENV is 'production' for all three. VERCEL_ENV would be the
// wrong gate here because it's unset outside Vercel, which would silently
// disable Airbrake for local prod builds.
export const isAirbrakeLive = process.env.NODE_ENV === 'production'

// Keys whose values Airbrake replaces with [Filtered] before sending.
//
// String entries match by EXACT key equality in airbrake-js (isInList in
// @airbrake/browser's jsonify_notice); only regex entries match partially. The
// old list of ['password', 'secret', 'token', 'key'] therefore let id_token,
// ssn, dob, publicToken, etc. straight through. Regexes over-match on purpose:
// for a fintech, a scrubbed debug field costs an internal lookup, a leaked one
// costs a breach analysis.
//
// LIMITATION (by SDK design): key filtering applies only to params /
// environment / session. notice.context — including breadcrumbs — is NEVER
// filtered, so nothing sensitive may be placed in context in the first place.
// Crawlers: their "errors" are rendering artifacts, not user-facing bugs.
// Lives here (SDK-free module) so both the browser filter and the server-side
// onRequestError hook can share it without importing a Notifier.
export const BOT_UA_REGEX =
  /AdsBot-Google-Mobile|Googlebot|AdsBot-Google|Google-Read-Aloud|bingbot|DuckDuckBot|Baiduspider|YandexBot|facebookexternalhit|Slackbot|LinkedInBot|Twitterbot/i

export const keysBlocklist = [
  /pass(word)?/i, // password, passwordConfirmation
  /token/i, // token, id_token, linkToken, publicToken, iframeToken, resetPasswordToken
  /secret/i,
  /ssn|social/i,
  /dob|birth/i,
  /^code$|otp|verification/i, // OAuth/SMS codes — anchored so postalCode/referralCode survive
  /card|cvv|csc|^pan$/i, // TabaPay card material
  /account.?number|routing/i,
  /^phone|^email/i, // identity is userId-only (deliberate GLBA-minimization decision)
  /^address\d?$|postal/i, // street-level address; administrativeArea (state) stays visible
  /authorization|cookie|session[_-]?id|fingerprint|udid/i,
  /answers?$/i, // data-vault AnswerQuestion payloads
  /^key$/i, // the literal key "key"
  /[_-]key([_-]?id)?$/i, // snake/kebab: encrypted_card_key_id, api-key
  /Key(Id)?$/, // camelCase: apiKey, projectKey, encryptedCardKeyId — case-SENSITIVE so "monkey"/"donkey" stay visible
]
